import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// Api
import api from '../api/Base';
import HostApi from '../api/host/HostApi';
import InstanceApi from '../api/instance/InstanceApi';

// Types
import Action from '../types/Action';
import HostTarget from '../types/host/HostTarget';
import InstanceTargetHost from '../types/instance/InstanceTargetHost';
import {
    HostFormData,
    HostFormState,
    HostFormValidation,
    createDefaultFormData,
    createDefaultValidation,
    createDefaultUIState,
    createDefaultDependencies
} from '../types/host/HostFormState';

// Utils
import { HostFormValidator, HostFormUtils } from '../helpers/HostFormValidation';
import { showError } from '../utils/showAlert';

// Constants
import { ACTIONS } from '../component/Constants';

interface UseHostFormProps {
    hosts: HostTarget[];
    setHosts: (hosts: HostTarget[]) => void;
}

export const useHostForm = ({ hosts, setHosts }: UseHostFormProps) => {
    const navigate = useNavigate();
    const { actionId, hostId } = useParams();

    // Main state
    const [formState, setFormState] = useState<HostFormState>({
        formData: createDefaultFormData(),
        validation: createDefaultValidation(),
        uiState: createDefaultUIState(actionId === ACTIONS.DELETE.type),
        dependencies: createDefaultDependencies()
    });

    const [action, setAction] = useState<Action>(ACTIONS.CREATE);

    // Update form data
    const updateFormData = useCallback((updates: Partial<HostFormData>) => {
        setFormState(prev => ({
            ...prev,
            formData: { ...prev.formData, ...updates }
        }));
    }, []);

    // Update validation
    const updateValidation = useCallback((updates: Partial<HostFormValidation>) => {
        setFormState(prev => ({
            ...prev,
            validation: { ...prev.validation, ...updates }
        }));
    }, []);

    // Update UI state
    const updateUIState = useCallback((updates: Partial<typeof formState.uiState>) => {
        setFormState(prev => ({
            ...prev,
            uiState: { ...prev.uiState, ...updates }
        }));
    }, []);

    // Update dependencies
    const updateDependencies = useCallback((updates: Partial<typeof formState.dependencies>) => {
        setFormState(prev => ({
            ...prev,
            dependencies: { ...prev.dependencies, ...updates }
        }));
    }, []);

    // Load host types and default values
    const loadHostTypes = useCallback(async () => {
        try {
            const entityType = await HostApi.getHostTypes();
            updateDependencies({ defaultValues: entityType });
        } catch (error) {
            console.error('Failed to load host types:', error);
        }
    }, [updateDependencies]);

    // Load agents
    const loadAgents = useCallback(async () => {
        try {
            const response = await api.get('agent');
            if (response?.data) {
                updateDependencies({ agents: response.data });
            }
        } catch (error) {
            console.error('Failed to load agents:', error);
        }
    }, [updateDependencies]);

    // Load mapped instances
    const loadMappedInstances = useCallback(async (hostId: number) => {
        try {
            const hostMappings = await InstanceApi.getInstanceHostByHost(hostId);
            const mappedInstanceIds = hostMappings.map((instance: InstanceTargetHost) => instance.id);
            updateFormData({ mappedInstances: mappedInstanceIds });
        } catch (error) {
            console.error('Failed to retrieve mapped instances for host.', error);
        }
    }, [updateFormData]);

    // Load host data
    const loadHost = useCallback((hostId: number) => {
        const host = hosts.find((h: HostTarget) => h.id === hostId);

        if (!host) {
            updateUIState({ isMissing: true });
            return;
        }

        updateFormData({
            id: host.id,
            name: host.name,
            hostAddress: host.host === null ? '' : host.host,
            type: host.type,
            agentId: host.agentid,
            monitor: host.monitor,
            debug: host.debug,
            port: host.port || 22,
            username: host.username,
            password: host.password === null ? '' : host.password,
            keyfile: host.keyfile === null ? '' : host.keyfile,
            region: host.region || '',
            monitoringInterval: host.monitoringintervalmilliseconds,
            encrypted: host.encrypted || false
        });
    }, [hosts, updateFormData, updateUIState]);

    // Set action based on actionId
    const setActionFromId = useCallback(() => {
        switch (actionId) {
            case 'create':
                setAction(ACTIONS.CREATE);
                break;
            case 'edit':
                setAction(ACTIONS.UPDATE);
                break;
            case 'delete':
                setAction(ACTIONS.DELETE);
                updateUIState({ disabled: true });
                break;
            default:
                console.error('Unknown action requested', actionId);
                break;
        }
    }, [actionId, updateUIState]);

    // Validation handlers
    const validateName = useCallback(() => {
        const validation = HostFormValidator.validateName(
                formState.formData.name,
                formState.formData.id,
                hosts
        );

        updateValidation({ name: validation });
        updateFormData({ name: formState.formData.name.trim() });
    }, [formState.formData.name, formState.formData.id, hosts, updateValidation, updateFormData]);

    const validateHostAddress = useCallback(() => {
        const validation = HostFormValidator.validateHostAddress(
                formState.formData.hostAddress
        );

        updateValidation({ hostAddress: validation });
        updateFormData({ hostAddress: formState.formData.hostAddress.trim() });
    }, [formState.formData.hostAddress, formState.formData.id, hosts, updateValidation, updateFormData]);

    const validatePort = useCallback(() => {
        const validation = HostFormValidator.validatePort(formState.formData.port);
        updateValidation({ port: validation });
    }, [formState.formData.port, updateValidation]);

    // Submit handler
    const handleSubmit = useCallback(async () => {
        updateUIState({ loading: true });

        // Validate the complete form
        const { isValid, validation } = HostFormValidator.validateCompleteForm(
                formState.formData,
                hosts
        );

        updateValidation(validation);

        if (!isValid) {
            updateUIState({ loading: false });
            return;
        }

        try {
            const data = [{
                id: formState.formData.id,
                name: formState.formData.name,
                host: formState.formData.hostAddress,
                agentid: formState.formData.agentId,
                type: formState.formData.type,
                monitor: formState.formData.monitor,
                debug: formState.formData.debug,
                port: Number(formState.formData.port),
                username: formState.formData.username === null ? '' : formState.formData.username,
                password: formState.formData.password === '' ? null : formState.formData.password,
                passwordchanged: formState.formData.passwordChanged,
                keyfile: formState.formData.keyfile === '' ? null : formState.formData.keyfile,
                region: formState.formData.region === '' ? null : formState.formData.region,
                monitoringintervalmilliseconds: formState.formData.monitoringInterval,
                encrypted: formState.formData.encrypted
            }];

            let result: any = undefined;

            switch (action) {
                case ACTIONS.CREATE:
                    // Save the object
                    result = await api.post('host', data);

                    if (result && result.status === 200) {
                        // Create any instance mappings
                        const instanceTargetHosts: InstanceTargetHost[] = formState.formData.mappedInstances.map(instanceId => ({
                            id: instanceId,
                            hostid: result.data[0]
                        }));

                        await api.post('datasource/host', instanceTargetHosts);
                    }
                    break;

                case ACTIONS.UPDATE:
                    // Update the object
                    result = await api.put('host', data);

                    // Delete all instance associations
                    await api.delete('datasource/host', { data: [{ 'hostid': formState.formData.id }] });

                    // Recreate the host and instance associations
                    const instanceTargetHosts: InstanceTargetHost[] = formState.formData.mappedInstances.map(instanceId => ({
                        id: instanceId,
                        hostid: formState.formData.id
                    }));

                    await api.post('datasource/host', instanceTargetHosts);
                    break;

                case ACTIONS.DELETE:
                    // Delete all instance associations
                    result = await api.delete('datasource/host', { data: [{ 'hostid': formState.formData.id }] });

                    if (result && result.status !== 200) {
                        console.error('Failed to complete requested action', action, result, data);
                        const errMsg = result.response?.data?.message || result.message || 'Failed to delete host associations';
                        showError('Failed to delete host', errMsg);
                        updateUIState({ loading: false });
                        return;
                    }

                    // Delete the object
                    result = await api.delete('host', { data });

                    // Get the latest hosts and update the context object
                    const updatedHosts = await HostApi.getHosts();
                    setHosts(updatedHosts);

                    // Redirect the user back to the list view
                    navigate(-1);
                    return;

                default:
                    console.error('Unknown action requested', action);
                    const unknownError = 'Unknown action requested';
                    showError('Failed to process host', unknownError);
                    updateUIState({ loading: false });
                    break;
            }

            if (result && result.status !== 200) {
                console.error('Failed to complete requested action', action, result, data);
                const actionName = action === ACTIONS.CREATE ? 'create' : action === ACTIONS.UPDATE ? 'update' : 'process';
                const errMsg = result.response?.data?.message || result.message || 'An error occurred while processing your request';
                showError(`Failed to ${actionName} host`, errMsg);
                updateUIState({ loading: false });
                return;
            }

            // Get the latest hosts and update the context object
            const updatedHosts = await HostApi.getHosts();
            setHosts(updatedHosts);

            // Redirect the user back to the last view
            navigate(-1);

        } catch (error: any) {
            console.error('Failed to complete requested action', action, error);
            const errMsg = error.response?.data?.message || error.message || error.toString();
            const actionName = action === ACTIONS.CREATE ? 'create' : action === ACTIONS.UPDATE ? 'update' : 'delete';
            showError(`Failed to ${actionName} host`, errMsg);
            updateUIState({ loading: false });
        }
    }, [formState, hosts, action, setHosts, navigate, updateUIState, updateValidation]);

    // Initialize form
    useEffect(() => {
        loadHostTypes();
        loadAgents();
        setActionFromId();

        if (hostId) {
            loadHost(Number(hostId));
            loadMappedInstances(Number(hostId));
        }
    }, [hostId, hosts, actionId]);

    // Update port and monitoring interval when type changes
    useEffect(() => {
        const hostDefaults = formState.dependencies.defaultValues.find(item => item.type === formState.formData.type);
        const host = hosts.find((h: HostTarget) => h.id === Number(hostId));

        if (!host && hostDefaults) {
            updateFormData({
                port: hostDefaults.defaultport || 22,
                monitoringInterval: hostDefaults.defaultmonitoringintervalmilliseconds
            });
        }
    }, [formState.dependencies.defaultValues, formState.formData.type, hostId, hosts, updateFormData]);

    return {
        formState,
        action,
        updateFormData,
        updateValidation,
        updateUIState,
        validateName,
        validateHostAddress,
        validatePort,
        handleSubmit,
        // Utility functions
        canViewPassword: () => HostFormUtils.canViewPassword(
                action === ACTIONS.CREATE,
                action === ACTIONS.UPDATE,
                formState.formData.passwordChanged
        )
    };
};

